// main components
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getCompanyProfile,
  getCompanyUsers,
  updateCompany
} from '../services/ApiService'
import { getManagementRoles } from '../lib/Api'

import { RolesCard } from '../components/companySettings/RolesCard'
import { UsersCard } from '../components/companySettings/UsersCard'
import { SupportCard } from '../components/companySettings/SupportCard'
import ConfirmationModal from '../components/general/ConfirmationModal'

/** Material UI **/
import {
  Typography,
  Container,
  Box,
  Card,
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material'

/** Redux **/
import { useSelector, useDispatch } from 'react-redux'
import { ProfileInfoCard } from '../components/companySettings/ProfileInfoCard'
import { CompanyProfileComponent } from '../components/companyprofile/CompanyProfileComponent'
import { dataActions } from '../store/data'

import {
  getBase64,
  validateEmail,
  isValidWebsite,
  validatePhone
} from '../lib/Global'
import {
  maxFileSize,
  minRatio,
  minResolution,
  maxRatio,
  imageExtensions
} from '../lib/Constants'
import { companySettingsStyles } from '../styles/classes/CompanySettingsClasses'

import { faBuilding } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Ring } from 'react-awesome-spinners'
import { userHasAuthorization } from '../services/AuthService'

const CompanySettings = props => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { classes } = companySettingsStyles()
  const userStore = useSelector(state => state.auth.user)
  const [company, setCompany] = useState(null)
  const [afterHoursPhone, setAfterHoursPhone] = useState(null)
  const [updatedCompany, setUpdatedCompany] = useState(null)
  const [roles, setRoles] = useState(null)
  const [ftcUsers, setFtcUsers] = useState([])
  const [mobileUsers, setMobileUsers] = useState([])
  const [open, setOpen] = useState(false)
  const [openConfirm, setOpenConfirm] = useState(false)
  const [logoData, setLogoData] = useState()
  const [component, setComponent] = useState('')

  const [dispatchError, setDispatchError] = useState(false)
  const [invoiceError, setInvoiceError] = useState(false)

  const [logoError, setLogoError] = useState()
  const [validHours, setValidHours] = useState(true)
  const [complianceFields, setComplianceFields] = useState({})
  const [loading, setLoading] = useState(false)
  const [reloadUsers, setReloadUsers] = useState(false)
  const [profileError, setProfileError] = useState({
    businessPhoneError: false,
    afterHoursPhoneError: false
  })

  useEffect(() => {
    initialMethod()
  }, [])

  const initialMethod = async () => {
    try {
      setComplianceFields(
        userStore.userInfo?.configurations?.onboarding?.compliance
      )
      setLoading(true)
      const response = await getCompanyProfile(userStore.userInfo.company_id)
      setLoading(false)
      setAfterHoursPhone(response?.after_hours?.phone)
      setCompany(response)
    } catch (error) {
      console.error('Error retrieving company profile: ', error)
      setLoading(false)
    }
    updateRoles()
    updateUsers()
  }

  useEffect(() => {
    setUpdatedCompany({ ...company })
  }, [company])

  useEffect(() => {
    handleValidations(updatedCompany)
  }, [validHours])

  useEffect(() => {
    if (reloadUsers) {
      updateUsers()
    }
  }, [reloadUsers])

  const updateRoles = async () => {
    try {
      const response = await getManagementRoles()
      setRoles(response)
    } catch (error) {
      console.error(error)
      setRoles([])
    }
  }

  const updateUsers = async () => {
    try {
      setMobileUsers([])
      setFtcUsers([])
      const response = await getCompanyUsers(userStore.userInfo.company_id)
      const mobile = response.filter(
        user => !user.roles || user.roles === 'no_value'
      )
      setMobileUsers(mobile)
      const ftc = response.filter(
        user => user.roles && user.roles !== 'no_value'
      )
      setFtcUsers(ftc)
    } catch (error) {
      console.error(error)
      setMobileUsers([])
      setFtcUsers([])
    }
  }

  const handleClose = () => {
    setOpen(false)
    setOpenConfirm(false)
  }

  const handleSave = async data => {
    try {
      setButtonDisabled(true)
      const newProfile = {
        ...data,
        invoice_email: data.invoice_email ?? '',
        w9: data.w9 ?? {},
        support_24_7: data.support_24_7 ?? false,
        service_area: data.service_area ?? [],
        country: data.country ?? []
      }
      delete newProfile.name
      delete newProfile.company
      delete newProfile.id
      delete newProfile.external_token
      delete newProfile.client_ids
      delete newProfile.service_request_enabled
      delete newProfile.request_cancel_reasons
      delete newProfile.dynamicTrades
      delete newProfile.industriesCatalog
      if (!newProfile.configs) {
        newProfile.configs = []
      }
      newProfile.compliance = calculateCompliance()
        ? parseInt(calculateCompliance().split('%')[0])
        : undefined
      newProfile.update_information = true
      // set step to control file upload only on Insurance info edit
      if (component === 'insurance') {
        newProfile.step = 1
      }
      if (newProfile?.clients) {
        newProfile.formatted_clients = [...newProfile?.clients]
          .map(client => {
            return t('company_profile.clients.' + client)
          })
          .join(', ')
      }
      if (newProfile?.states_registered) {
        newProfile.formatted_states = [...newProfile?.states_registered]
          .map(state => {
            return state.license
              ? state.state + ' - ' + state.license
              : state.state
          })
          .join(', ')
      }
      if (newProfile?.contacts && newProfile?.contacts.length > 0) {
        newProfile?.contacts.forEach(element => {
          delete element.send_email
        })
      }
      await updateCompany(userStore.userInfo.company_id, newProfile)
      // get updated object
      const response = await getCompanyProfile(userStore.userInfo.company_id)
      dispatch(dataActions.setCompanyData(response))
      setAfterHoursPhone(response?.after_hours?.phone)
      setCompany(response)
      setOpen(false)
    } catch (error) {
      console.error(error)
      setButtonDisabled(false)
    }
  }

  function editComponent (targetComponent) {
    switch (targetComponent) {
      case 'profile':
        return (
          <CompanyProfileComponent
            profile={updatedCompany}
            handleChange={handleChange}
            showLogo={false}
            dispatchError={dispatchError}
            invoiceError={invoiceError}
            setValidHours={setValidHours}
            requiredFields={complianceFields?.information?.fields}
            afterHoursPhone={afterHoursPhone}
            profileError={profileError}
          />
        )
      default:
        break
    }
  }

  const descriptionElementRef = useRef(null)
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef
      if (descriptionElement !== null) {
        descriptionElement.focus()
      }
    }
  }, [open])

  const handleChange = (value, field) => {
    let data = { ...updatedCompany }
    if (!field) return
    if (field.match(/coi_.*/)) {
      const tempData = { ...data?.coi }
      tempData[field] = value
      data.coi = { ...tempData }
    } else if (field.match(/comp_.*/)) {
      const tempData = { ...data?.comp }
      tempData[field] = value
      data.comp = { ...tempData }
    } else if (field.match(/bank_.*/)) {
      const tempData = { ...data?.bank }
      tempData[field] = value
      data.bank = { ...tempData }
    } else if (field.match(/q_.*/)) {
      const tempData = { ...data?.insurance_questions }
      tempData[field] = value
      data.insurance_questions = { ...tempData }
    } else if (field === 'country') {
      const countries = typeof value === 'string' ? value.split(',') : value
      data.country = countries
    } else {
      data = { ...data, [field]: value }
    }
    setUpdatedCompany(prevValues => {
      return { ...prevValues, ...data }
    })
    handleValidations(data)
  }

  function equalObjects (object1, object2) {
    if (!object1 || !object2) return false
    const keys1 = Object.keys(object1)
    const keys2 = Object.keys(object2)
    if (keys1.length !== keys2.length) {
      return false
    }
    for (const key of keys1) {
      const val1 = object1[key]
      const val2 = object2[key]
      const areObjects = isObject(val1) && isObject(val2)

      if (
        (areObjects && !equalObjects(val1, val2)) ||
        (!areObjects && val1 !== val2)
      ) {
        return false
      }
    }
    return true
  }
  function isObject (object) {
    return object != null && typeof object === 'object'
  }

  const calculateCompliance = () => {
    if (!company) return
    if (!userStore.userInfo?.configurations?.onboarding?.compliance) return

    const requirements = userStore.userInfo.configurations.onboarding.compliance

    let total = 0
    // COMPANY INFORMATION
    for (const field in requirements.information.fields) {
      if (company[field]) {
        total +=
          (requirements.information.section_total *
            requirements.information.fields[field]) /
          100
      }
    }

    // COMPANY INSURANCE
    for (const field in requirements.insurance.fields) {
      if (company[field]) {
        total +=
          (requirements.insurance?.section_total *
            requirements.insurance.fields[field]) /
          100
      }
    }

    // COMPANY TRADES AND SERVICES
    if (
      Array.isArray(company?.trades) &&
      company?.trades?.length > 0 &&
      company?.trades[0].services?.length > 0
    ) {
      total += requirements.tradesServices.section_total
    }

    // COMPANY SERVICE AREA
    if (
      Array.isArray(company?.service_area) &&
      company?.service_area.length > 0
    ) {
      total += requirements.serviceArea.section_total
    }

    // COMPANY USERS
    if (mobileUsers.length + ftcUsers.length > 0) {
      total += requirements.users.section_total
    }

    return total + '% ' + t('company_settings.compliant')
  }

  const handleImageChange = async event => {
    const file = event.target.files[0]
    const fileSize = file.size / 1024 / 1024
    if (file) {
      const reader = new FileReader()

      reader.onload = e => {
        const image = new Image()
        image.src = e.target.result

        image.onload = async () => {
          // check file size
          if (fileSize > maxFileSize) {
            setLogoError(
              t('company_profile.error.file_size').replace(
                '{size}',
                maxFileSize
              )
            )
            setOpenConfirm(true)
            return
          }
          // check file resolution
          if (image.width < 1024 || image.height < 1024) {
            setLogoError(
              t('company_profile.error.file_resolution').replace(
                '{res}',
                minResolution
              )
            )
            setOpenConfirm(true)
            return
          }
          // check image aspect ratio
          if (
            minRatio > image.width / image.height ||
            image.width / image.height > maxRatio
          ) {
            setLogoError(t('company_profile.error.file_aspect_ratio'))
            setOpenConfirm(true)
            return
          }
          setLogoError(null)
          try {
            getBase64(file, result => openConfirmDialog(result))
          } catch (error) {
            console.log(error)
          }
        }
      }
      reader.readAsDataURL(file)
    }
    event.target.value = null
  }

  const openConfirmDialog = imageData => {
    setLogoData(imageData)
    setOpenConfirm(true)
  }

  const updateProfileLogo = async () => {
    const data = { ...company }
    data.tech_support = { ...data.tech_support, customer_logo: logoData }
    setUpdatedCompany(data)
    handleClose()
    await handleSave(data)
    setAfterHoursPhone(data?.after_hours?.phone)
    setCompany(data)
  }
  const [buttonDisabled, setButtonDisabled] = useState(true)

  const handleValidations = data => {
    switch (component) {
      case 'profile':
        setButtonDisabled(!profileValidation(data))
        break
      default:
        setButtonDisabled(true)
        break
    }
  }

  const profileValidation = data => {
    const errors = { ...profileError }
    if (!data.name) {
      return false
    }
    if (data?.email && !validateEmail(data?.email)) {
      setDispatchError(true)
      return false
    }
    if (data?.website && !isValidWebsite(data?.website)) {
      setInvoiceError(true)
      return false
    }
    // phone validation
    if (
      (data?.business_hours?.phone &&
        !validatePhone(data?.business_hours?.phone)) ||
      (data?.after_hours?.phone && !validatePhone(data?.after_hours?.phone))
    ) {
      errors.businessPhoneError = !validatePhone(data?.business_hours?.phone)
      errors.afterHoursPhoneError = !validatePhone(data?.after_hours?.phone)
      setProfileError(errors)
      return false
    }
    setProfileError({
      businessPhoneError: false,
      afterHoursPhoneError: false
    })
    setDispatchError(false)
    setInvoiceError(false)
    return true
  }

  return (
    <Container
      maxWidth={false}
      data-testid={'company_settings_page'}
      className={classes.container}
    >
      <Box className={classes.background} />
      <Box display="flex" flexDirection="row">
        <Typography classes={{ root: classes.title }}>
          {t('company_settings.title')}
        </Typography>
      </Box>
      <Box display="flex" className={classes.cardsContainer}>
        <Box flex={1}>
          {/* Logo card */}
          <Card data-testid="logo_card" className={classes.card}>
            <Box display="flex" flexDirection="row">
              {company?.tech_support?.customer_logo && (
                <label htmlFor="profile-logo" className={classes.labelUpload}>
                  <Avatar
                    alt="profile"
                    src={company?.tech_support?.customer_logo ?? null}
                    className={classes.avatar}
                    imgProps={{ style: { objectFit: 'contain' } }}
                  ></Avatar>
                  <Container className={classes.logoButton}>
                    <br />
                    <Typography classes={{ root: classes.logoText }}>
                      {t('company_settings.logo_message')}
                    </Typography>
                  </Container>
                </label>
              )}
              {!company?.tech_support?.customer_logo && loading && (
                <Ring className={classes.ring} />
              )}
              {!company?.tech_support?.customer_logo && !loading && (
                <label htmlFor="profile-logo" className={classes.labelUpload}>
                  <Avatar alt="profile" className={classes.emptyAvatar}>
                    <FontAwesomeIcon icon={faBuilding} size="3x" />
                  </Avatar>
                  <Container className={classes.logoButton}>
                    {userHasAuthorization('company_settings:write') && (
                      <Button
                        id="profile-logo"
                        component="label"
                        className={classes.uploadButton}
                      >
                        {t('company_settings.upload_logo')}
                        <input
                          hidden
                          accept={imageExtensions.join(', ')}
                          type="file"
                          onChange={handleImageChange}
                        />
                      </Button>
                    )}
                    <Typography classes={{ root: classes.uploadLogoText }}>
                      {t('company_settings.logo_message')}
                    </Typography>
                  </Container>
                </label>
              )}
              {company?.tech_support?.customer_logo &&
                userHasAuthorization('company_settings:write') && (
                  <label htmlFor="profile-logo" className={classes.editButton}>
                    <Button
                      id="profile-logo"
                      component="label"
                      className={classes.editButton}
                    >
                      {t('company_settings.buttons.edit')}
                      <input
                        hidden
                        accept="image/png, image/jpeg, image/jpg, image/bitmap"
                        type="file"
                        onChange={handleImageChange}
                      />
                    </Button>
                  </label>
              )}
            </Box>
          </Card>
          {/* Profile card */}
          <ProfileInfoCard
            profile={company}
            setOpen={setOpen}
            setComponent={setComponent}
          />
          <RolesCard updateRoles={updateRoles} roles={roles} />
          <SupportCard company={company} />
        </Box>
        <Box flex={1} marginBottom="4em">
          {/* Web portal users */}
          <UsersCard
            roles={roles}
            company={company}
            cardtitle={t('company_settings.card.users')}
            users={ftcUsers}
            updateUsers={updateUsers}
            mobile={false}
            setReloadUsers={setReloadUsers}
          />
        </Box>
      </Box>
      {/* Edit profile dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        className={classes.editComponent}
        maxWidth={'sm'}
      >
        <DialogContent>{editComponent(component)}</DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => handleSave(updatedCompany)}
            className={classes.saveButton}
            disabled={equalObjects(company, updatedCompany) || buttonDisabled}
          >
            {t('company_settings.card.save')}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        title={t('company_settings.card.update')}
        message={logoError ?? t('company_settings.card.update_question')}
        open={openConfirm}
        onMainAction={updateProfileLogo}
        onSecondaryAction={handleClose}
        mainActionLabel={t('company_settings.card.save')}
        secondaryActionLabel={t('company_settings.card.cancel')}
        hideMainAction={!!logoError}
      />
    </Container>
  )
}

export default CompanySettings
